/* eslint-disable max-lines */
import enc from "crypto-js/enc-hex";
import SHA256 from "crypto-js/sha256";
import addMonths from "date-fns/addMonths";
import endOfDay from "date-fns/endOfDay";
import startOfDay from "date-fns/startOfDay";
import Cookies from "js-cookie";
import type { NextPageContext, PageProps } from "next";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import type { GraphQLTaggedNode } from "react-relay";
import { graphql, readInlineData, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { scmpPlus } from "scmp-sdk";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { data as appData, article as articleData } from "shared/data";
import { section as sectionData } from "shared/data/section";
import { Edition, parseEdition } from "shared/lib/edition";
import { theme } from "shared/lib/styles";
import { isAfterOrEqual, isBeforeOrEqual, notEmpty, parseQueryString } from "shared/lib/utils";

import {
  TargetingHomePageTakeOverType,
  TargetingPaidType,
} from "scmp-app/components/advertisement/ad-slots/types";
import { isArticleVideoAsHero } from "scmp-app/components/content/content-cover-video/helpers";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { HomeV2ClientQueryLoader } from "scmp-app/components/home-v2/client-query-loader";
import { MidPageWidgets } from "scmp-app/components/home-v2/mid-page-widgets";
import { HomeV2Newsletter } from "scmp-app/components/home-v2/newsletter";
import { HomeV2PlusWidget } from "scmp-app/components/home-v2/plus-widget";
import { type Section } from "scmp-app/components/home-v2/section-top";
import { HomeV2SuperSplash } from "scmp-app/components/home-v2/super-splash";
import { HomeV2TrendingTopic } from "scmp-app/components/home-v2/trending-topic";
import { HomeSeo } from "scmp-app/components/home/seo";
import { useHomepageMidPageRecommendationConfig } from "scmp-app/components/homepage-mid-page-recommendation/hooks";
import { usePlusWidgetConfig } from "scmp-app/components/plus/plus-widget/hooks";
import { AiEngineDataType } from "scmp-app/lib/ai-engine/enums";
import {
  useAutoRefreshWhenIdlingForTenMinutes,
  useGetEntityIdsByQueueItems,
} from "scmp-app/lib/hooks";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import {
  HeroArticle,
  HeroSecondaryArticles,
  StyledAdSlot,
  StyledContentItemHomeNativeArticle,
  TopStories,
} from "scmp-app/pages/home/styles";
import type {
  homePageQuery,
  LiveWidgetEditionEnum,
} from "scmp-app/queries/__generated__/homePageQuery.graphql";
import type { homeSuperSplashInlineQuery$key } from "scmp-app/queries/__generated__/homeSuperSplashInlineQuery.graphql";

import { AiWidgetFetch } from "./ai-widget-fetch";
import { FallbackRandomSeed } from "./consts";
import {
  usePickMorningStudioArticles,
  usePremiumNativeAd,
  useTopStoriesNativeAdImpressionTracking,
  useTopStoriesTracking,
} from "./hooks";
import {
  AdvertisersWidget,
  AIWidget1,
  AIWidget2,
  AroundWidget,
  CFTWidgetPlaceholder,
  Container,
  ContentContainer,
  ContentWrapper,
  DesktopBannerAds,
  DesktopSidebarAdsBottom,
  DesktopSidebarAdsTop,
  Divider1,
  Divider2,
  Divider3,
  FlexiUnitContainer,
  FlexiUnitWidget,
  LatestWidget,
  LatestWidgetAds,
  MobileFooterAds,
  MostPopularWidget,
  MostPopularWidgetAds,
  MultimediaColumn,
  MultimediaWidget,
  MultimediaWidgetAds,
  NewsletterWidget,
  OpinionWidget,
  OpinionWidgetAds,
  PremiumAds,
  SCMPShortsWidget,
  SectionBlock1,
  SectionBlock2,
  SectionBlock3,
  SectionBlock4,
  SectionBlock5,
  SpotlightWidgetPlaceholder,
  StoryPackageOneWidgetPlaceholder,
  StoryPackageTwoWidgetPlaceholder,
  StyledAppFooter,
  StyledTakeoverAdSlot,
  SubscriptionWidget,
  ThingsToDoWidget,
  ThingsToDoWidgetAds,
  TopBand,
  TopBandDivider,
  TopBandLeft,
  TopBandRight,
  TopStoriesAds,
} from "./styles";

const homePageQuery: GraphQLTaggedNode = graphql`
  query homePageQuery(
    $applicationId: String!
    $commentHarrySectionIds: [String]
    $commentQueueName: String!
    $dailyPulseTypeUuId: String!
    $focusQueueName: String!
    $highlightQueueName: String!
    $latestExcludeArticleTypes: [String]
    $latestExcludeSectionIds: [String]
    $liveWidgetEditionEnumValue: LiveWidgetEditionEnum!
    $morningStudioQueueName: String!
    $plusNewsAgendaEndDate: Timestamp!
    $plusNewsAgendaStartDate: Timestamp!
    $plusNewsAgendaTypes: [String]!
    $scmpPlusPaywallTypeIds: [String]
    $scmpShortsQueueName: String!
    $storyPackageArticlesQueueName: String!
    $topStoriesQueueName: String!
  ) {
    topStories: queue(filter: { name: $topStoriesQueueName }) {
      items(first: 18) {
        edges {
          node {
            ... on Content {
              entityId
            }
            ...heroArticlesHomeV2Content
          }
          ...heroSecondaryArticlesHomeV2QueueItemsEdge
          ...hooksTopStoriesTrackingQueueItemsEdge
          ...topStoriesHomeV2QueueItemsEdge
        }
      }
    }
    morningStudioQueue: queue(filter: { name: $morningStudioQueueName }) {
      ...hooksHomeV2PickMorningStudioArticle
    }
    ...latestContentsHomeV2Query
      @arguments(
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
        latestExcludeSectionIds: $latestExcludeSectionIds
        latestExcludeArticleTypes: $latestExcludeArticleTypes
      )
    ...thingsToDoHomeV2Query
    ...commentQueueQuery
      @arguments(
        commentQueueName: $commentQueueName
        commentHarrySectionIds: $commentHarrySectionIds
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
      )
    ...opinionHomeV2QueueQuery
      @arguments(
        commentQueueName: $commentQueueName
        commentHarrySectionIds: $commentHarrySectionIds
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
      )
    ...scmpShortsHomeV2Query @arguments(scmpShortsQueueName: $scmpShortsQueueName)
    ...focusArticleHomeFocusArticleQuery @arguments(focusQueueName: $focusQueueName)
    ...flexiUnitHomeV2QueueQuery @arguments(focusQueueName: $focusQueueName)
    ...superSplashHomeV2Query @arguments(edition: $liveWidgetEditionEnumValue)
    ...homeSuperSplashInlineQuery @arguments(edition: $liveWidgetEditionEnumValue)
    ...plusWidgetQuery
      @arguments(
        applicationId: $applicationId
        dailyPulseTypeUuId: $dailyPulseTypeUuId
        highlightQueueName: $highlightQueueName
        plusNewsAgendaEndDate: $plusNewsAgendaEndDate
        plusNewsAgendaStartDate: $plusNewsAgendaStartDate
        plusNewsAgendaTypes: $plusNewsAgendaTypes
      )
    ...chinaScienceDiscoveryWidgetQuery
    ...hooksPlusWidgetConfigQuery
    ...hooksHomepageMidPageRecommendationConfigQuery
    ...plusWidgetHomeV2Query
    ...hooksPremiumNativeAdConfigQuery

    commentQueue: queue(filter: { name: $commentQueueName }) {
      items(first: 5) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    topStoriesEntityIds: queue(filter: { name: $topStoriesQueueName }) {
      items(first: 18) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    storyPackageArticles: queue(filter: { name: $storyPackageArticlesQueueName }) {
      items(first: 3) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    flexiUnitEntityIds: queue(filter: { name: $focusQueueName }) {
      items(first: 1) {
        edges {
          ...contentUtilitiesGetEntityIdsByQueueItemQueueItemsEdge
        }
      }
    }
    # Preloaded for the header one line menu, which reads this record from the store.
    pwaOnelineMenu: appConfig(filter: { entityId: "scmp_pwa_oneline_menu" }) {
      # eslint-disable-next-line relay/unused-fields
      json
    }
  }
`;

const sectionBlocksByEdition: Record<Edition, Section[]> = {
  [Edition.Asia]: ["twia", "people-culture", "china", "lifestyle", "style"],
  [Edition.HongKong]: ["hong-kong", "lifestyle", "china", "wellness", "style"],
  [Edition.International]: ["hong-kong", "lifestyle", "china", "people-culture", "style"],
};
const sectionBlockComponents = [
  SectionBlock1,
  SectionBlock2,
  SectionBlock3,
  SectionBlock4,
  SectionBlock5,
];

const HomePage: RelayComponent<PageProps, homePageQuery> = ({ preloadedQuery, randomSeed }) => {
  const queryReference = usePreloadedQuery(homePageQuery, preloadedQuery);

  const { query: routeQuery } = useRouter();
  const edition = routeQuery.edition as Edition;
  const adZone = useMemo(() => `homepage_${edition}/home`, [edition]);

  const { isStoryPackageOneAiEnabled, isStoryPackageTwoAiEnabled } =
    useHomepageMidPageRecommendationConfig(queryReference, edition);

  const sectionBlocks = useMemo(() => {
    const blocks = sectionBlocksByEdition[edition];
    const count = Math.min(blocks.length, sectionBlockComponents.length);
    return blocks.slice(0, count).map((section, index) => ({
      Component: sectionBlockComponents[index],
      section,
    }));
  }, [edition]);

  useAutoRefreshWhenIdlingForTenMinutes();

  const ssrContentEntityIds = useGetEntityIdsByQueueItems([
    ...(queryReference.commentQueue?.items?.edges ?? []),
    ...(queryReference.storyPackageArticles?.items?.edges ?? []),
    ...(queryReference.topStoriesEntityIds?.items?.edges ?? []),
    ...(queryReference.flexiUnitEntityIds?.items?.edges ?? []),
  ]);

  const [aiWidgetEntityIds, setAiWidgetEntityIds] = useState<null | string[]>(null);

  const handleAiWidgetReady = useCallback((entityIds: string[]) => {
    setAiWidgetEntityIds(entityIds);
  }, []);

  const mergedEntityIds = useMemo(
    () =>
      aiWidgetEntityIds ? [...ssrContentEntityIds, ...aiWidgetEntityIds] : ssrContentEntityIds,
    [ssrContentEntityIds, aiWidgetEntityIds],
  );

  const fallbackQueryVariables = useMemo(
    () => ({
      chinaFutureTechSectionEntityId: sectionData.chinaFutureTech.entityId,
      chinaFutureTechStoryPackageArticlesQueueName: `section_top_${sectionData.chinaFutureTech.entityId}`,
      includeStoryPackageOne: true,
      includeStoryPackageTwo: true,
      packageTopStoryPackageArticlesQueueName: `homepage_package_top_${edition}`,
      spotlightQueueName: `scmp_signatures_top_${edition}`,
      storyPackageTwoQueueName: `homepage_package_top_2_${edition}`,
    }),
    [edition],
  );

  const { slot1: morningStudioArticleSlot1, slot2: morningStudioArticleSlot2 } =
    usePickMorningStudioArticles(
      randomSeed ?? FallbackRandomSeed,
      queryReference.morningStudioQueue,
    );
  const topStoriesSponsorArticleReference = morningStudioArticleSlot1 ?? null;
  const recommendedForYouSponsorArticleReference = morningStudioArticleSlot2 ?? null;

  // The Top Stories queue is shared with the hero widgets: edges[0] is the lead
  // hero article, edges[1..2] are the hero's secondary row, and edges[3..17]
  // (15 articles, given the query's `first: 18`) feed the Top Stories block in
  // order. The sponsored morning-studio article renders as a native-ad
  // side-card inside Top Stories (see HomeV2TopStories) rather than occupying
  // one of the article rows.
  const topStoryEdges = useMemo(
    () =>
      (queryReference.topStories?.items?.edges ?? [])
        .filter(edge => notEmpty(edge.node))
        .slice(0, 18),
    [queryReference.topStories?.items?.edges],
  );
  const [heroEdge, heroSecondaryEdge1, heroSecondaryEdge2, ...topStoriesEdges] = topStoryEdges;
  const heroSecondaryEdges = useMemo(
    () => [heroSecondaryEdge1, heroSecondaryEdge2],
    [heroSecondaryEdge1, heroSecondaryEdge2],
  );

  const {
    captureTrackImpressionEventElement1,
    captureTrackImpressionEventElement2,
    captureTrackImpressionEventElement3,
    captureTrackImpressionEventElement4,
    captureTrackImpressionEventElement5,
    sendGA4ClickEvent,
    sendGA4SponsorClickEvent,
  } = useTopStoriesTracking(topStoryEdges);

  const getImpressionTrackingFunction = useCallback(
    (index: number) => {
      switch (index) {
        case 0:
          return captureTrackImpressionEventElement2;
        case 4:
          return captureTrackImpressionEventElement3;
        case 8:
          return captureTrackImpressionEventElement4;
        case 12:
          return captureTrackImpressionEventElement5;
      }
    },
    [
      captureTrackImpressionEventElement2,
      captureTrackImpressionEventElement3,
      captureTrackImpressionEventElement4,
      captureTrackImpressionEventElement5,
    ],
  );

  const captureFirstNativeAdImpressionEvent = useTopStoriesNativeAdImpressionTracking();

  const { plusWidgetConfig } = usePlusWidgetConfig(queryReference);
  // The highlight widget is subscriber-only (gated by `hasScmpPlusAccessRight`
  // inside HomeV2PlusWidget), so the relevant disable schedule is `withAccess`.
  const isPlusDisabled = useMemo(
    () =>
      plusWidgetConfig?.withAccess?.disabledDateRanges?.some(({ end, start }) => {
        const now = new Date();
        return isAfterOrEqual(now, start) && (notEmpty(end) ? isBeforeOrEqual(now, end) : true);
      }) ?? false,
    [plusWidgetConfig],
  );

  const isEnabledPremiumNativeAd = usePremiumNativeAd(queryReference);

  // The super splash and the hero article both render an inline video player
  // when they hold a lead video. Two eager-preloaded YouTube iframes on the
  // same viewport is too heavy, so the hero falls back to its poster link
  // whenever the super splash above it is playing a video.
  const superSplashData = readInlineData<homeSuperSplashInlineQuery$key>(
    graphql`
      fragment homeSuperSplashInlineQuery on Query
      @argumentDefinitions(edition: { type: "LiveWidgetEditionEnum!" })
      @inline {
        liveWidget(
          filter: { edition: $edition }
          orderBy: { direction: DESC, field: UPDATED_DATE }
        ) {
          relatedArticles {
            ... on Article {
              ...helpersIsArticleVideoAsHeroArticle
            }
          }
        }
      }
    `,
    queryReference,
  );
  const superSplashFirstArticle = superSplashData?.liveWidget?.relatedArticles?.[0] ?? null;
  const isSuperSplashVideo = superSplashFirstArticle
    ? isArticleVideoAsHero(superSplashFirstArticle)
    : false;

  return (
    <>
      <HomeSeo edition={edition} />
      <Container>
        <ContentContainer>
          <HomeV2SuperSplash reference={queryReference} />
          <ContentWrapper $edition={edition}>
            <TopBand>
              <TopBandLeft>
                {notEmpty(heroEdge?.node) && (
                  <HeroArticle
                    clickTrackingFunction={sendGA4ClickEvent}
                    ref={captureTrackImpressionEventElement1}
                    reference={heroEdge.node}
                    withVideoClickToPlay={!isSuperSplashVideo}
                  />
                )}
                {notEmpty(heroSecondaryEdges) && (
                  <HeroSecondaryArticles
                    clickTrackingFunction={sendGA4ClickEvent}
                    reference={heroSecondaryEdges}
                  />
                )}
                {notEmpty(topStoriesEdges) && (
                  <TopStories
                    clickTrackingFunction={sendGA4ClickEvent}
                    getImpressionTrackingFunction={getImpressionTrackingFunction}
                    reference={topStoriesEdges}
                    slots={{
                      mobileInlineAds: (
                        <StyledAdSlot
                          adUnit="m_lrec1"
                          breakpoint={theme.breakpoints.only("mobile")}
                          sizes={[[300, 250]]}
                          targeting={{
                            paid: TargetingPaidType.NotArticle,
                          }}
                          withBackground
                          withLabel
                          withLabelBackground
                          withSticky
                          zone={adZone}
                        />
                      ),
                      nativeAds: (
                        <>
                          <StyledAdSlot
                            adUnit="d_native2c"
                            breakpoint={theme.breakpoints.up("desktop")}
                            sizes={[[1, 1], "fluid"]}
                            targeting={{ paid: TargetingPaidType.NotArticle }}
                            zone={adZone}
                          />
                          <StyledAdSlot
                            adUnit="m_native2c"
                            breakpoint={theme.breakpoints.down("desktop")}
                            sizes={[[1, 1], "fluid"]}
                            targeting={{ paid: TargetingPaidType.NotArticle }}
                            zone={adZone}
                          />
                        </>
                      ),
                      plusWidget: !isPlusDisabled && (
                        <HomeV2PlusWidget reference={queryReference} />
                      ),
                      sponsorArticle: notEmpty(topStoriesSponsorArticleReference?.node) && (
                        <StyledContentItemHomeNativeArticle
                          onClick={sendGA4SponsorClickEvent}
                          ref={captureFirstNativeAdImpressionEvent}
                          reference={topStoriesSponsorArticleReference?.node}
                        />
                      ),
                    }}
                  />
                )}
              </TopBandLeft>
              <TopBandDivider />
              <TopBandRight>
                <FlexiUnitContainer>
                  <FlexiUnitWidget reference={queryReference} />

                  {isEnabledPremiumNativeAd && (
                    <PremiumAds>
                      <StyledAdSlot
                        adUnit="m_native2f"
                        breakpoint={theme.breakpoints.down("desktop")}
                        sizes={[[1, 1], "fluid"]}
                        targeting={{ paid: TargetingPaidType.NotArticle }}
                        zone={adZone}
                      />
                      <StyledAdSlot
                        adUnit="d_native2f"
                        breakpoint={theme.breakpoints.up("desktop")}
                        sizes={[[1, 1], "fluid"]}
                        targeting={{ paid: TargetingPaidType.NotArticle }}
                        zone={adZone}
                      />
                    </PremiumAds>
                  )}
                </FlexiUnitContainer>

                <TopStoriesAds>
                  <StyledAdSlot
                    adUnit="m_banner1"
                    breakpoint={theme.breakpoints.only("mobile")}
                    sizes={[[300, 250]]}
                    targeting={{
                      paid: TargetingPaidType.NotArticle,
                    }}
                    withBackground
                    withLabel
                    withLabelBackground
                    withSticky
                    zone={adZone}
                  />
                  <StyledAdSlot
                    adUnit="m_lrec1"
                    breakpoint={theme.breakpoints.only("tablet")}
                    sizes={[[300, 250]]}
                    targeting={{
                      paid: TargetingPaidType.NotArticle,
                    }}
                    withBackground
                    withLabel
                    withLabelBackground
                    withSticky
                    zone={adZone}
                  />
                  <StyledAdSlot
                    adUnit="d_lrec1"
                    breakpoint={theme.breakpoints.up("desktop")}
                    sizes={[[300, 250]]}
                    targeting={{
                      paid: TargetingPaidType.NotArticle,
                    }}
                    withLabel
                    withLabelBackground
                    zone={adZone}
                  />
                </TopStoriesAds>

                <OpinionWidget reference={queryReference} />
                <OpinionWidgetAds>
                  <StyledAdSlot
                    adUnit="m_lrec3"
                    breakpoint={theme.breakpoints.down("desktop")}
                    sizes={[
                      [300, 250],
                      [320, 480],
                    ]}
                    targeting={{
                      paid: TargetingPaidType.NotArticle,
                    }}
                    withBackground
                    withLabel
                    withLabelBackground
                    withSticky
                    zone={adZone}
                  />
                  <StyledAdSlot
                    adUnit="d_lrec3"
                    breakpoint={theme.breakpoints.up("desktop")}
                    sizes={[
                      [300, 250],
                      [300, 600],
                    ]}
                    targeting={{
                      paid: TargetingPaidType.NotArticle,
                    }}
                    withLabel
                    withLabelBackground
                    withMaxContainerSize={false}
                    zone={adZone}
                  />
                </OpinionWidgetAds>
                <DesktopSidebarAdsTop>
                  <StyledAdSlot
                    adUnit="d_lrec3a"
                    breakpoint={theme.breakpoints.up("desktop")}
                    sizes={[
                      [300, 250],
                      [300, 600],
                    ]}
                    targeting={{
                      paid: TargetingPaidType.NotArticle,
                    }}
                    withLabel
                    withLabelBackground
                    zone={adZone}
                  />
                </DesktopSidebarAdsTop>

                <DesktopSidebarAdsBottom>
                  <StyledAdSlot
                    adUnit="d_lrec3b"
                    breakpoint={theme.breakpoints.up("desktop")}
                    sizes={[[300, 250]]}
                    targeting={{
                      paid: TargetingPaidType.NotArticle,
                    }}
                    withLabel
                    withLabelBackground
                    zone={adZone}
                  />
                </DesktopSidebarAdsBottom>
              </TopBandRight>
            </TopBand>

            <ErrorBoundary fallback={<></>}>
              <NewsletterWidget>
                <HomeV2Newsletter />
              </NewsletterWidget>
            </ErrorBoundary>

            <LatestWidget reference={queryReference} />
            <ThingsToDoWidget reference={queryReference} />

            <AiWidgetFetch
              fallbackQueryVariables={fallbackQueryVariables}
              onReady={handleAiWidgetReady}
              renderPlaceholders={() => (
                <>
                  <StoryPackageOneWidgetPlaceholder />
                  <StoryPackageTwoWidgetPlaceholder />
                  <CFTWidgetPlaceholder />
                  <SpotlightWidgetPlaceholder />
                </>
              )}
              renderWidgets={(
                data,
                fallbackData,
                slotModes,
                topicNames,
                fallbackReason,
                widgetVersion,
                recommendationId,
              ) => (
                <MidPageWidgets
                  data={data}
                  fallbackData={fallbackData}
                  fallbackReason={fallbackReason}
                  recommendationId={recommendationId}
                  slotModes={slotModes}
                  topicNames={topicNames}
                  widgetVersion={widgetVersion}
                />
              )}
              ssrContentEntityIds={ssrContentEntityIds}
              useAiForStoryPackageOneSlot={isStoryPackageOneAiEnabled}
              useAiForStoryPackageTwoSlot={isStoryPackageTwoAiEnabled}
            />
            <SCMPShortsWidget reference={queryReference} />

            <LatestWidgetAds>
              <StyledAdSlot
                adUnit="m_lrec3c"
                breakpoint={theme.breakpoints.down("desktop")}
                sizes={[
                  [300, 250],
                  [320, 480],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withLabelBackground
                withSticky
                zone={adZone}
              />
              <StyledAdSlot
                adUnit="d_lrec2"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[
                  [300, 250],
                  [300, 600],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withLabel
                withLabelBackground
                withMaxContainerSize={false}
                zone={adZone}
              />
            </LatestWidgetAds>

            <ThingsToDoWidgetAds>
              <StyledAdSlot
                adUnit="m_lrec2"
                breakpoint={theme.breakpoints.down("desktop")}
                sizes={[
                  [300, 250],
                  [320, 480],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                withLabelBackground
                withSticky
                zone={adZone}
              />
              <StyledAdSlot
                adUnit="d_lrec4"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[
                  [300, 250],
                  [300, 600],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withLabel
                withLabelBackground
                zone={adZone}
              />
            </ThingsToDoWidgetAds>

            <DesktopBannerAds>
              <StyledAdSlot
                adUnit="d_banner5"
                breakpoint={theme.breakpoints.up("desktop")}
                sizes={[
                  [970, 250],
                  [728, 90],
                  [970, 90],
                ]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                zone={adZone}
              />
            </DesktopBannerAds>

            <Divider1 />

            <ErrorBoundary fallback={<></>}>
              <ClientSideSuspense fallback={<></>}>
                {aiWidgetEntityIds !== null && (
                  <HomeV2ClientQueryLoader ssrContentEntityIds={mergedEntityIds}>
                    {({ aiWidgets, data, existingEntityIds }) => {
                      const advertisersReference = data.advertisers?.items;

                      const hasRecommendedForYou =
                        aiWidgets[AiEngineDataType.HomeRecommendedForYou].entityIds.length > 0;

                      const hasYouMayHaveMissed =
                        aiWidgets[AiEngineDataType.HomeYouMayHaveMissed].entityIds.length > 0;

                      return (
                        <>
                          {hasRecommendedForYou && (
                            <AIWidget1
                              aiEngineResponse={aiWidgets[AiEngineDataType.HomeRecommendedForYou]}
                              slots={{
                                sponsorArticle(tracking, ref) {
                                  return (
                                    notEmpty(recommendedForYouSponsorArticleReference?.node) && (
                                      <StyledContentItemHomeNativeArticle
                                        onClick={tracking}
                                        ref={ref}
                                        reference={recommendedForYouSponsorArticleReference?.node}
                                      />
                                    )
                                  );
                                },
                              }}
                              variant={AiEngineDataType.HomeRecommendedForYou}
                            />
                          )}
                          {hasYouMayHaveMissed && (
                            <AIWidget2
                              aiEngineResponse={aiWidgets[AiEngineDataType.HomeYouMayHaveMissed]}
                              slots={{
                                nativeAds: (
                                  <>
                                    <StyledAdSlot
                                      adUnit="d_native2a"
                                      breakpoint={theme.breakpoints.up("desktop")}
                                      customizedAdUnitTargeting="dnative-6"
                                      sizes={[[1, 1], "fluid"]}
                                      targeting={{ paid: TargetingPaidType.NotArticle }}
                                      zone={adZone}
                                    />
                                    <StyledAdSlot
                                      adUnit="m_native2a"
                                      breakpoint={theme.breakpoints.down("desktop")}
                                      customizedAdUnitTargeting="dnative-6"
                                      sizes={[[1, 1], "fluid"]}
                                      targeting={{ paid: TargetingPaidType.NotArticle }}
                                      zone={adZone}
                                    />
                                  </>
                                ),
                              }}
                              variant={AiEngineDataType.HomeYouMayHaveMissed}
                            />
                          )}
                          {advertisersReference && (
                            <AdvertisersWidget reference={advertisersReference} />
                          )}
                          <AroundWidget
                            existingEntityIds={existingEntityIds}
                            randomSeed={randomSeed ?? FallbackRandomSeed}
                          />
                          <MostPopularWidget
                            reference={data}
                            trendingTopicSlot={<HomeV2TrendingTopic reference={data} />}
                          />
                          <MultimediaColumn>
                            <MultimediaWidget reference={data} />
                            <MultimediaWidgetAds>
                              <StyledAdSlot
                                adUnit="m_lrec3g"
                                breakpoint={theme.breakpoints.down("desktop")}
                                sizes={[
                                  [300, 250],
                                  [320, 480],
                                ]}
                                targeting={{
                                  paid: TargetingPaidType.NotArticle,
                                }}
                                withBackground
                                withLabel
                                withLabelBackground
                                withSticky
                                zone={adZone}
                              />
                              <StyledAdSlot
                                adUnit="d_lrec4a"
                                breakpoint={theme.breakpoints.up("desktop")}
                                sizes={[[300, 250]]}
                                targeting={{
                                  paid: TargetingPaidType.NotArticle,
                                }}
                                withLabel
                                withLabelBackground
                                withMaxContainerSize={false}
                                zone={adZone}
                              />
                            </MultimediaWidgetAds>
                          </MultimediaColumn>
                          <SubscriptionWidget />

                          {sectionBlocks.map(({ Component, section }) => (
                            <Component key={section} reference={data} section={section} />
                          ))}

                          <MostPopularWidgetAds>
                            <StyledAdSlot
                              adUnit="m_lrec4"
                              breakpoint={theme.breakpoints.down("desktop")}
                              sizes={[
                                [300, 250],
                                [320, 480],
                              ]}
                              targeting={{
                                paid: TargetingPaidType.NotArticle,
                              }}
                              withBackground
                              withLabel
                              withLabelBackground
                              withSticky
                              zone={adZone}
                            />
                            <StyledAdSlot
                              adUnit="d_lrec4b"
                              breakpoint={theme.breakpoints.up("desktop")}
                              sizes={[
                                [300, 250],
                                [300, 600],
                              ]}
                              targeting={{
                                paid: TargetingPaidType.NotArticle,
                              }}
                              withLabel
                              withLabelBackground
                              withMaxContainerSize={false}
                              zone={adZone}
                            />
                          </MostPopularWidgetAds>
                          <MobileFooterAds>
                            <StyledAdSlot
                              adUnit="m_lrec3h"
                              breakpoint={theme.breakpoints.only("mobile")}
                              sizes={[
                                [300, 250],
                                [320, 480],
                              ]}
                              targeting={{
                                paid: TargetingPaidType.NotArticle,
                              }}
                              withBackground
                              withLabel
                              withLabelBackground
                              withSticky
                              zone={adZone}
                            />
                          </MobileFooterAds>
                          <Divider2 />
                          <Divider3 />
                        </>
                      );
                    }}
                  </HomeV2ClientQueryLoader>
                )}
                <StyledAppFooter />
              </ClientSideSuspense>
            </ErrorBoundary>
          </ContentWrapper>

          <StyledTakeoverAdSlot adSlotProps={{ zone: adZone }} variant="home" />
        </ContentContainer>
      </Container>
    </>
  );
};

HomePage.displayName = "HomePage";

const getPageProps = (context: NextPageContext) => {
  const edition = parseQueryString(context.query.edition) as Edition | undefined;
  const adZone = `homepage_${edition ?? "int"}/home`;

  const pageProps: PageProps = {
    appBarConfiguration: {
      desktopBreakpoint: "desktop",
      hasDesktopAd: true,
      hasMobileAd: true,
      hasTabletAd: false,
      topBannerAdSlot: {
        desktop: {
          adUnit: "d_banner1",
          sizes: [
            [970, 250],
            [728, 90],
            [970, 90],
          ],
          targeting: {
            hpto: TargetingHomePageTakeOverType.Top,
            paid: TargetingPaidType.NotArticle,
          },
          zone: adZone,
        },
        mobile: {
          adUnit: "m_banner3",
          sizes: [
            [300, 100],
            [320, 100],
            [300, 50],
            [320, 50],
          ],
          targeting: {
            hpto: TargetingHomePageTakeOverType.Top,
            paid: TargetingPaidType.NotArticle,
          },
          zone: adZone,
        },
      },
      variant: "scmp/home",
    },
    initEdition:
      edition ?? parseEdition(context.req?.cookies?.scmp_edition ?? Cookies.get("scmp_edition")),
    isBot: context.query.isBot === "1",
  };

  return pageProps;
};

const generateRandomSeed = () => SHA256(new Date().toString()).toString(enc);

export default withRelay(HomePage, homePageQuery, {
  clientSideProps: context => ({
    ...getPageProps(context),
    randomSeed: generateRandomSeed(),
  }),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: async context => {
    const pageProps = Promise.resolve(getPageProps(context));
    return {
      ...(await pageProps),
      randomSeed: generateRandomSeed(),
    };
  },
  variablesFromContext(context) {
    const edition = parseQueryString(context.query.edition) ?? "int";
    const liveWidgetEditionEnumValue: LiveWidgetEditionEnum =
      edition.toLocaleUpperCase() as Uppercase<Edition>;
    const startDate = startOfDay(new Date()).getTime();
    const endDate = addMonths(endOfDay(new Date()), 1).getTime();

    return {
      applicationId: appData.application.scmp.entityId,
      commentHarrySectionIds: [sectionData.comment.harrysView.entityUuid],
      commentQueueName: `opinion_top_${edition}`,
      dailyPulseTypeUuId: articleData.types.dailyPulseBriefing.entityUuid,
      edition,
      focusQueueName: `homepage_focus_${edition}`,
      highlightQueueName: "scmp_plus_home_news",
      latestExcludeArticleTypes: [
        articleData.types.series.entityUuid,
        articleData.types.letters.entityUuid,
        articleData.types.recap.entityUuid,
      ],
      latestExcludeSectionIds: [
        sectionData.posties.entityUuid,
        sectionData.spark.entityUuid,
        sectionData.youngPost.entityUuid,
        sectionData.yp.entityUuid,
        sectionData.recap.entityUuid,
        sectionData.cooking.entityUuid,
      ],
      liveWidgetEditionEnumValue,
      morningStudioQueueName: `homepage_morning_studio_${edition}`,
      plusNewsAgendaEndDate: endDate,
      plusNewsAgendaStartDate: startDate,
      plusNewsAgendaTypes: [scmpPlus.topicType.newsAgenda],
      scmpPlusPaywallTypeIds: [scmpPlus?.article?.paywallTypeId],
      scmpShortsQueueName: "homepage_video_shorts",
      storyPackageArticlesQueueName: `homepage_package_top_${edition}`,
      topStoriesQueueName: `homepage_top_${edition}`,
    };
  },
});
